import { useQuizBrowserBack } from './quizHistoryGuard';
import { QUIZ_ROUTES } from "./quizRoutes";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import { useQuizBatchAnalytics } from "../../hooks/useQuizBatchAnalytics";
import {
  buildQuizBackTarget,
  buildQuizFlowState,
  getStoredQuizFlowState,
  persistQuizFlowState,
  navigateQuizBack,
  resolveQuizBackTarget,
} from "./quizFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../DashboardSkeleton.css";
import "./ResultAnalysis.css";
import HeaderProfileSection from "../HeaderProfileSection";
import SubjectCard from "./SubjectCard";
import PerformanceInsights from "./PerformanceInsights";

const ResultAnalysisShimmer = () => (
  <div className="qbl-page analysis-page analysis-shimmer" aria-hidden="true">
    <div>
      <div className="result-test-card result-test-card--shimmer">
        <div className="result-test-left">
          <span className="dashboard-skeleton dashboard-skeleton--thumb analysis-shimmer__avatar" />
          <div className="result-test-info analysis-shimmer__info">
            <span className="dashboard-skeleton dashboard-skeleton--button analysis-shimmer__status" />
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title analysis-shimmer__title" />
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short analysis-shimmer__meta" />
          </div>
        </div>

        <div className="result-test-stats">
          {[0, 1, 2].map((index) => (
            <div className="result-stat-box analysis-shimmer__stat" key={`analysis-stat-${index}`}>
              <span className="dashboard-skeleton dashboard-skeleton--line analysis-shimmer__statValue" />
              <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short analysis-shimmer__statLabel" />
            </div>
          ))}
        </div>
      </div>

      <div className="analysis-shimmer__subjectGrid">
        {[0, 1, 2, 3].map((index) => (
          <div className="subject-card analysis-shimmer__subjectCard" key={`analysis-subject-${index}`}>
            <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title analysis-shimmer__subjectTitle" />
            <span className="dashboard-skeleton dashboard-skeleton--line analysis-shimmer__subjectCopy" />
            <div className="analysis-shimmer__progressRow">
              <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short analysis-shimmer__progressLabel" />
              <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short analysis-shimmer__progressValue" />
            </div>
            <span className="dashboard-skeleton dashboard-skeleton--line analysis-shimmer__progressBar" />
            <div className="analysis-shimmer__metricsRow">
              {[0, 1, 2].map((metricIndex) => (
                <span
                  className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short analysis-shimmer__metric"
                  key={`analysis-metric-${index}-${metricIndex}`}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="analysis-shimmer__sidebar">
      <div className="analysis-shimmer__panel">
        <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title analysis-shimmer__panelTitle" />
        <div className="analysis-shimmer__panelRows">
          {[0, 1, 2, 3].map((index) => (
            <div className="analysis-shimmer__panelRow" key={`analysis-panel-row-${index}`}>
              <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short analysis-shimmer__panelLabel" />
              <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short analysis-shimmer__panelValue" />
            </div>
          ))}
        </div>
      </div>

      <div className="analysis-shimmer__panel">
        <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title analysis-shimmer__panelTitle analysis-shimmer__panelTitle--short" />
        <div className="analysis-shimmer__guessCard">
          <span className="dashboard-skeleton dashboard-skeleton--line analysis-shimmer__guessScore" />
          <span className="dashboard-skeleton dashboard-skeleton--line analysis-shimmer__guessCopy" />
          <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short analysis-shimmer__guessCopy analysis-shimmer__guessCopy--short" />
        </div>
      </div>
    </div>
  </div>
);

const ResultAnalysis = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const persistedFlowState = React.useMemo(() => getStoredQuizFlowState(), []);
  const flowState = React.useMemo(
    () => buildQuizFlowState(location.state, persistedFlowState),
    [location.state, persistedFlowState]
  );
  const quizData = flowState?.quizData || {};
  const quizId = Number(flowState?.quiz_id || quizData?.quizId || quizData?.quiz_id || quizData?.id || 0);
  const assesmentBatchId = Number(flowState?.assesment_batch_id || 0);
  const resolvedBackTarget = resolveQuizBackTarget(flowState, QUIZ_ROUTES.resultAnalysis, buildQuizBackTarget(QUIZ_ROUTES.answerSheet, flowState));
  useQuizBrowserBack(resolvedBackTarget);

  const resultAnalysisScreenState = React.useMemo(
    () =>
      buildQuizFlowState({
        ...flowState,
        quizBackTarget: resolvedBackTarget,
      }),
    [flowState, resolvedBackTarget]
  );

  React.useEffect(() => {
    persistQuizFlowState(resultAnalysisScreenState);
  }, [resultAnalysisScreenState]);

  const analyticsQuery = useQuizBatchAnalytics(
    {
      quiz_id: quizId,
      assesment_batch_id: assesmentBatchId,
    },
    {
      enabled: quizId > 0 && assesmentBatchId > 0,
    }
  );

  const analytics = analyticsQuery.data;
  const quizDetail = analytics?.quizDetail || {};
  const summary = analytics?.summary || {};
  const subjectWisePerformance = analytics?.subjectWisePerformance || [];
  const performanceInsights = analytics?.performanceInsights || {};
  const guessInsights = analytics?.guessInsights || {};

  const handleGoBack = () => {
    navigateQuizBack(navigate, resolvedBackTarget);
  };

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }
           .homeDashboard-wrapper .main-content{
        height: 100vh;
        overflow: scroll;
        box-sizing: border-box;
        padding-bottom: 35px;
        min-height: 0;
      }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbankList-page page-padding fixed-page-btn">
          <div className="start-quiz qbl-main instruction-main ">
            <div className="hideProfile-page ">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={handleGoBack}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
                <div>
                  <h4 className="d-flex categories-page-title"> Result Analysis </h4>
                </div>
                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            {analyticsQuery.isError ? (
              <div className="quiz-empty mt-3">
                <i className="bi bi-exclamation-circle" />
                <h3>Unable to load result analysis</h3>
                <p>{analyticsQuery.error?.message || "Please try again."}</p>
              </div>
            ) : analyticsQuery.isLoading || analyticsQuery.isFetching ? (
              <ResultAnalysisShimmer />
            ) : (
              <div className="qbl-page analysis-page">
                <div>
                  <div className="result-test-card">
                    <div className="result-test-left">
                      <img
                        src={quizDetail?.userImageUrl || "https://i.pravatar.cc/150?img=12"}
                        alt={quizDetail?.userName || "Student"}
                        className="result-student-img"
                      />

                      <div className="result-test-info">
                        <span className="result-completed">
                          <span className="result-check"><i className="bi bi-check"></i></span>
                          {quizDetail?.status || "COMPLETED"}
                        </span>

                        <h2>{quizDetail?.quizName || "Quiz"}</h2>

                        <p>
                          {quizDetail?.submittedDate || "-"}
                          <span>{quizDetail?.submittedTime || "-"}</span>
                        </p>
                      </div>
                    </div>

                    <div className="result-test-stats">
                      <div className="result-stat-box result-score">
                        <strong>{summary?.score || 0}/{summary?.maxScore || 0}</strong>
                        <span>Score</span>
                      </div>

                      <div className="result-stat-box result-correct">
                        <strong>{summary?.correctAnswers || 0}</strong>
                        <span>Correct</span>
                      </div>

                      <div className="result-stat-box result-incorrect">
                        <strong>{summary?.wrongAnswers || 0}</strong>
                        <span>Incorrect</span>
                      </div>
                    </div>
                  </div>

                  <SubjectCard subjects={subjectWisePerformance} />
                </div>

                <div>
                  <PerformanceInsights
                    totalTime={performanceInsights?.totalTimeTaken || "00:00:00"}
                    answeringTime={performanceInsights?.timeForAnswering || "00:00:00"}
                    reviewingTime={performanceInsights?.timeForReviewing || "00:00:00"}
                    averageTime={performanceInsights?.averageTimePerAnswer || "00:00:00"}
                    guessInsights={guessInsights}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default ResultAnalysis;
